import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  CheckCircle2, ArrowRight, ShieldCheck, TrendingUp, Users, PenTool, Database, Wallet, UserCheck, Monitor, Wrench, ShoppingCart, Store, ClipboardList,
  MonitorSmartphone, PackageSearch, Shuffle, Receipt, Calculator, PieChart, CarFront, Hammer, Droplets, Frame, Factory, Truck, Smartphone, CreditCard, GraduationCap, Instagram
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { formatCpfCnpj, formatPhone } from "../lib/validators";
import { SEO } from "../components/SEO";
import { recentPosts, heroPosts } from "./Blog";

export const Home = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="O ERP nº 1 para Oficinas Mecânicas" 
        description="O RosTech une a potência de um ERP robusto com a inteligência de um CRM. Abandone as planilhas e tenha controle total do estoque, financeiro e clientes em um só lugar."
        canonicalUrl="/"
        keywords="ERP oficina mecanica, sistema para oficina, gestão de oficina"
      />
      <HeroSection />
      <ModulesSection />
      <TrustSection />
      <RobustShowcaseSection />
      <BlogSection />
      <InstagramSection />
    </div>
  );
};

const HeroSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', document: '' });
  
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = `Olá equipe RosTech! Gostaria de agendar uma demonstração do sistema.\n*Nome:* ${formData.name}\n*E-mail:* ${formData.email}\n*Telefone:* ${formData.phone}\n*CNPJ/CPF:* ${formData.document}`;
    window.open(`https://wa.me/5524992818632?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <section className="relative text-slate-900 overflow-hidden bg-slate-50 lg:min-h-[480px] flex items-stretch pt-4 lg:pt-8 pb-20 lg:pb-24">
      {/* Fundo direito azul escuro */}
      <div className="absolute inset-y-0 right-0 w-full lg:w-[35%] bg-[#0A2540] z-0 hidden lg:block"></div>
      <div className="absolute bottom-0 left-0 right-0 h-[60%] bg-[#0A2540] z-0 lg:hidden"></div>

      {/* Divisória Vertical Completa */}
      <div className="absolute inset-y-0 left-[65%] w-px bg-slate-300 z-0 hidden lg:block"></div>

      <div className="w-full relative z-10 flex flex-col lg:flex-row items-center">
        {/* Esquerda */}
        <div className="w-full lg:w-[65%] px-6 md:px-12 xl:px-16 flex flex-col justify-center py-4 lg:py-6 lg:min-h-full">
          <div className="w-full max-w-2xl mx-auto lg:ml-auto lg:mr-10 xl:mr-16">
            <div className="inline-flex items-baseline mb-3 text-blue-700 bg-blue-100/80 px-3 py-1.5 rounded-full text-xs font-semibold">
              O ERP nº 1 para Oficinas Mecânicas
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-3 leading-[1.1]">
              Sua oficina organizada, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-800">seu lucro maximizado.</span>
            </h1>
            <p className="text-base md:text-lg text-slate-600 mb-5 leading-relaxed max-w-xl">
              O RosTech une a potência de um ERP robusto com a inteligência de um CRM. Abandone as planilhas e tenha controle total do estoque, financeiro e clientes em um só lugar.
            </p>
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <a href="https://wa.me/5524992818632" target="_blank" rel="noopener noreferrer">
                 <Button size="lg" className="w-full sm:w-auto h-12 bg-blue-700 hover:bg-blue-800 text-base font-bold">Falar com Consultor</Button>
              </a>
            </div>
          </div>
        </div>

        {/* Direita */}
        <div className="w-full lg:w-[35%] px-6 md:px-10 flex flex-col items-center justify-start mt-6 lg:mt-0 pb-6 lg:pt-6 relative lg:min-h-full">
          <div className="w-full max-w-[420px] lg:-mt-6 bg-white rounded-[16px] shadow-xl relative z-10 border border-slate-100 overflow-hidden">
            <div className="bg-red-600 text-white px-4 py-2 text-center text-[11px] font-bold tracking-wide uppercase leading-snug">
              CONDIÇÕES ESPECIAIS: AGENDE UMA DEMOSTRAÇÃO E GANHE 15 DIAS GRÁTIS
            </div>
            <div className="p-5 md:p-6 pb-6">
              <h3 className="text-2xl font-bold mb-1 text-slate-900">Solicite sua Demonstração</h3>
              <p className="text-slate-500 text-sm mb-4">Cadastre-se e entraremos rapidamente em contato.</p>
              <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">Nome completo</label>
                <input required placeholder="João Silva" className="flex h-10 w-full rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2 text-sm font-bold text-slate-900 placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-colors shadow-sm cursor-text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700">E-mail profissional</label>
                  <input type="email" required placeholder="joao@oficina.com.br" className="flex h-10 w-full rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2 text-sm font-bold text-slate-900 placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-colors shadow-sm cursor-text" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </div>
                <div className="flex-1 space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700">WhatsApp</label>
                  <input required placeholder="(11) 99999-9999" className="flex h-10 w-full rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2 text-sm font-bold text-slate-900 placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-colors shadow-sm cursor-text" value={formData.phone} onChange={e => setFormData({...formData, phone: formatPhone(e.target.value)})} maxLength={15} />
                </div>
              </div>
              <div className="pt-2">
                <Button type="submit" className="w-full h-11 bg-blue-700 hover:bg-blue-800 text-white shadow text-base font-bold transition-all hover:-translate-y-0.5">
                  Agendar Demonstração
                </Button>
              </div>
            </form>
           </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ModulesSection = () => {
  const modules = [
    {
      title: "Módulo ERP",
      description: "Integração total do administrativo e financeiro da sua oficina.",
      icon: <Database className="w-8 h-8 text-teal-600" strokeWidth={1.5} />,
      bg: "bg-teal-50"
    },
    {
      title: "PDV Oficial",
      description: "Vendas rápidas no balcão com emissão de NFC-e em um clique.",
      icon: <Monitor className="w-8 h-8 text-amber-500" strokeWidth={1.5} />,
      bg: "bg-amber-50"
    },
    {
      title: "Gestão CRM",
      description: "Ferramentas para fidelizar clientes e alavancar o retorno.",
      icon: <Users className="w-8 h-8 text-blue-500" strokeWidth={1.5} />,
      bg: "bg-blue-50"
    },
    {
      title: "Painel de OS",
      description: "Controle de ponta a ponta dos serviços, mecânicos e peças.",
      icon: <Wrench className="w-8 h-8 text-indigo-500" strokeWidth={1.5} />,
      bg: "bg-indigo-50"
    }
  ];

  return (
    <section className="relative w-full z-20 -mt-12 lg:-mt-24">
      {/* Fundo azul por trás dos cards */}
      <div className="absolute inset-x-0 bottom-12 lg:bottom-20 top-12 lg:top-24 bg-blue-700 z-0"></div>
      
      <div className="max-w-[1400px] mx-auto relative z-20 pb-16 pt-4 lg:pb-20 px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 pt-6 pb-12">
          {modules.map((mod, i) => (
            <div key={i} className="w-full bg-white rounded-2xl p-6 shadow-[0_15px_40px_-15px_rgba(0,0,0,0.1)] border border-slate-100 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] flex flex-col items-start text-left">
              <div className="flex items-center gap-4 mb-4 w-full">
                <div className={`shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center ${mod.bg}`}>
                  {mod.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 leading-tight pr-4">{mod.title}</h3>
              </div>
              <p className="text-slate-500 text-[15px] leading-relaxed mb-6 flex-1">
                {mod.description}
              </p>
              <Link to="/cadastro" className="text-blue-700 font-bold text-sm flex items-center group mt-auto">
                Descubra mais 
                <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TrustSection = () => {
  const logos = [
    "JadLog", "Sedex", "Correios", "Jamef", "NFE.io", "FocusNFE",
    "JadLog", "Sedex", "Correios", "Jamef", "NFE.io", "FocusNFE"
  ];
  
  return (
    <section className="py-10 bg-white border-b border-slate-100 overflow-hidden relative z-10 -mt-12 lg:-mt-20">
      <div className="max-w-[1400px] mx-auto px-6 flex items-center">
        {/* Título Fixo */}
        <div className="shrink-0 pr-8 md:pr-12 md:mr-4 z-10 hidden sm:block">
          <p className="text-base md:text-lg font-bold text-slate-800 tracking-wide uppercase">
            Parcerias:
          </p>
        </div>
        
        <div className="sm:hidden shrink-0 pr-4 z-10 w-full mb-4 text-center absolute top-0 left-0 pt-4">
           {/* Mobile view top text if needed, or simply don't show the text or show it differently */}
        </div>

        {/* Marquee Container */}
        <div className="flex-1 overflow-hidden relative sm:border-l border-slate-200 sm:pl-8">
          <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none sm:left-8"></div>
          <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
          
          <div className="flex w-max animate-marquee items-center opacity-60">
            {/* Array is looped manually for seamless scrolling */}
            <div className="flex gap-16 md:gap-24 items-center pr-16 md:pr-24">
              {logos.map((logo, i) => (
                <span key={i} className="text-xl md:text-3xl font-black text-slate-400 hover:text-slate-600 transition-colors uppercase tracking-tighter whitespace-nowrap">{logo}</span>
              ))}
            </div>
            <div className="flex gap-16 md:gap-24 items-center pr-16 md:pr-24">
              {logos.map((logo, i) => (
                <span key={`dup-${i}`} className="text-xl md:text-3xl font-black text-slate-400 hover:text-slate-600 transition-colors uppercase tracking-tighter whitespace-nowrap">{logo}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeatureRow = ({ title, description, url, reverse = false, icon: Icon, children }: any) => (
  <div className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 py-20 border-b border-slate-100 last:border-0`}>
    <div className="flex-1 space-y-6">
      <div className="inline-flex items-center px-4 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold tracking-widest uppercase mb-2 shadow-sm border border-blue-100">
        <Icon className="w-4 h-4 mr-2" /> {url}
      </div>
      <h3 className="text-3xl font-extrabold text-slate-900 tracking-tight">{title}</h3>
      <p className="text-slate-600 text-lg leading-relaxed">{description}</p>
    </div>
    <div className="flex-1 w-full">
      <div className="bg-white/80 backdrop-blur-xl border border-slate-200/60 rounded-2xl shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] p-6 overflow-hidden">
        <div className="flex gap-2 mb-6 border-b border-slate-100 pb-4">
          <div className="w-3 h-3 rounded-full bg-rose-400"></div>
          <div className="w-3 h-3 rounded-full bg-amber-400"></div>
          <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
        </div>
        {children}
      </div>
    </div>
  </div>
);

const RobustShowcaseSection = () => {
  const [activeTab, setActiveTab] = useState(0);

  const industrias = [
    { title: "Oficina Mecânica", desc: "Gestão completa para o dia a dia automotivo, com ordens de serviço detalhadas e histórico online.", icon: <Wrench className="w-6 h-6" />, image: "https://images.unsplash.com/photo-1619642751034-765dfdf7c162?w=1200&q=80", href: "/industrias/oficina-mecanica" },
    { title: "Autopeças e Moto", desc: "Controle de estoque preciso, vendas no balcão dinâmicas e integração com catálogos.", icon: <ShoppingCart className="w-6 h-6" />, image: "https://images.unsplash.com/photo-1555626906-fcf10d6851b4?w=1200&q=80", href: "/industrias/autopecas" },
    { title: "Centro Automotivo", desc: "CRM e agendamentos otimizados para alto fluxo de clientes e serviços rápidos.", icon: <CarFront className="w-6 h-6" />, image: "https://images.unsplash.com/photo-1632823469837-1425121bdfc0?w=1200&q=80", href: "/industrias/centro-automotivo" },
    { title: "Funilaria e Pintura", desc: "Gestão de orçamentos complexos, controle de peças e aprovações de seguradoras.", icon: <Hammer className="w-6 h-6" />, image: "https://images.unsplash.com/photo-1605814561849-c167df3c9424?w=1200&q=80", href: "/industrias/funilaria" },
    { title: "Lava Jato", desc: "Controle diário rápido, fila virtual de lavagem, gestão de pacotes e comissões.", icon: <Droplets className="w-6 h-6" />, image: "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=1200&q=80", href: "/industrias/lava-jato" },
    { title: "Auto Glass", desc: "Reparos de vidros especiais, catálogos de peças e integração com de seguradoras.", icon: <Frame className="w-6 h-6" />, image: "https://images.unsplash.com/photo-1554223090-7e482851df45?w=1200&q=80", href: "/industrias/auto-glass" }
  ];

  const solucoes = [
    { title: "Vende online", desc: "Gerencie todo seu processo de vendas online, integre com plataformas e emita notas fiscais corporativas.", icon: <ShoppingCart className="w-6 h-6 text-emerald-600" />, bg: "bg-emerald-50", border: "border-emerald-100" },
    { title: "Tem loja física", desc: "Emita notas, controle estoque com código de barras e gerencie frente de caixa na velocidade da luz.", icon: <Store className="w-6 h-6 text-rose-600" />, bg: "bg-rose-50", border: "border-rose-100" },
    { title: "Oferece serviço", desc: "Cadastre clientes, controle ordens de serviço, envie orçamentos via WhatsApp e links de pagamento.", icon: <Users className="w-6 h-6 text-blue-600" />, bg: "bg-blue-50", border: "border-blue-100" },
    { title: "Pequena indústria", desc: "Gerencie fornecedores, suprimentos, OP (ordem de produção) e custo real de cada material produzido.", icon: <Factory className="w-6 h-6 text-indigo-600" />, bg: "bg-indigo-50", border: "border-indigo-100" }
  ];

  const recursos = [
    { title: "Envios Integrados", desc: "Cotação e emissão de etiquetas integradas aos Correios e transportadoras com a Melhor Envio.", icon: <Truck className="w-10 h-10 text-emerald-600" /> },
    { title: "Conta Digital", desc: "Conta digital PJ grátis integrada. Gere boletos e Pix pelo próprio RosTech com conciliação automática.", icon: <Smartphone className="w-10 h-10 text-blue-600" /> },
    { title: "Maquininha POS", desc: "Sua própria maquininha inteligente. Receba pagamentos integrados direto na O.S. e evite perdas.", icon: <CreditCard className="w-10 h-10 text-amber-600" /> },
    { title: "Mentoria Elite", desc: "Acelere seus resultados com nossa mentoria exclusiva voltada para proprietários do ramo automotivo.", icon: <GraduationCap className="w-10 h-10 text-rose-600" /> }
  ];

  return (
    <>
      {/* Ecosystem Section */}
      <section className="bg-[#0f172a] pt-32 pb-40 relative overflow-hidden text-white border-t border-slate-800">
        <div className="absolute top-0 right-0 -translate-y-1/3 translate-x-1/3 w-[800px] h-[800px] bg-blue-900/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          
          <div className="text-center max-w-4xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 font-semibold text-sm mb-6 tracking-wide">
              <Database className="w-4 h-4"/> Eossistema Completo
            </div>
            <h2 className="text-4xl lg:text-5xl font-black tracking-tight mb-8 leading-tight">
              Uma plataforma, <br /> infinitas possibilidades.
            </h2>
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto">
              Navegue pelas funcionalidades do sistema e descubra como o RosTech acelera cada etapa do seu dia a dia operacional.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
             {/* Major Card (span 2) - Ordem de Serviço */}
             <div className="lg:col-span-2 bg-[#1e293b]/80 border border-slate-700/50 rounded-3xl p-8 lg:p-12 overflow-hidden relative group backdrop-blur-sm">
                <div className="relative z-10 w-full md:w-3/5 text-white">
                   <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mb-8 border border-blue-400/30">
                     <Wrench className="w-8 h-8 text-blue-400" />
                   </div>
                   <h4 className="text-3xl lg:text-4xl font-black mb-5 tracking-tight text-slate-100">Ordem de Serviço</h4>
                   <p className="text-slate-300 text-lg leading-relaxed mb-8">
                     O coração da sua operação. Controle serviços, peças, status de aprovação, atribua mecânicos e envie orçamentos interativos via WhatsApp em segundos.
                   </p>
                   <button className="text-blue-400 font-bold flex items-center gap-2 group-hover:gap-3 transition-all">
                     Descubra na prática <ArrowRight className="w-5 h-5" />
                   </button>
                </div>
                {/* CSS Mockup Background */}
                <div className="absolute -right-10 top-20 md:top-14 w-[500px] rotate-[-5deg] group-hover:rotate-0 transition-transform duration-700 pointer-events-none opacity-40 md:opacity-100">
                   <div className="bg-[#0f172a] border border-slate-700 p-6 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] space-y-5">
                     <div className="flex items-center justify-between border-b border-slate-800 pb-5">
                        <div className="flex items-center gap-4">
                           <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                             <Wrench className="w-6 h-6 text-amber-500" />
                           </div>
                           <div>
                             <div className="text-white font-bold text-lg">OS #2049</div>
                             <div className="text-amber-400 text-sm font-semibold mt-0.5">Em Andamento</div>
                           </div>
                        </div>
                        <div className="px-3 py-1.5 bg-slate-800 rounded-lg text-slate-300 text-xs font-mono border border-slate-700">Honda Civic</div>
                     </div>
                     <div className="space-y-4 pt-2">
                        <div className="flex gap-4 items-center bg-slate-800/50 p-4 rounded-2xl border border-slate-700/50">
                           <div className="w-10 h-10 bg-blue-500/30 rounded-xl"></div>
                           <div className="flex-1 space-y-2.5">
                             <div className="w-full h-2.5 bg-slate-600 rounded-full"></div>
                             <div className="w-1/2 h-2.5 bg-slate-700 rounded-full"></div>
                           </div>
                        </div>
                        <div className="flex gap-4 items-center bg-slate-800/50 p-4 rounded-2xl border border-slate-700/50">
                           <div className="w-10 h-10 bg-purple-500/30 rounded-xl"></div>
                           <div className="flex-1 space-y-2.5">
                             <div className="w-4/5 h-2.5 bg-slate-600 rounded-full"></div>
                             <div className="w-2/3 h-2.5 bg-slate-700 rounded-full"></div>
                           </div>
                        </div>
                     </div>
                   </div>
                </div>
             </div>

             {/* Minor Card 1 - Financeiro */}
             <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/20 rounded-3xl p-8 lg:p-10 group overflow-hidden relative flex flex-col">
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-amber-500/30 backdrop-blur-md">
                    <Wallet className="w-8 h-8 text-amber-500" />
                  </div>
                  <h4 className="text-2xl font-bold text-slate-100 mb-4">Financeiro & DRE</h4>
                  <p className="text-slate-400 leading-relaxed mb-6">
                    Acompanhe receitas, gerencie despesas e preveja seu fluxo de caixa diário.
                  </p>
                </div>
                {/* CSS Mockup Background */}
                <div className="mt-auto relative h-32 bg-amber-500/10 rounded-t-2xl border-t border-x border-amber-500/20 translate-y-8 group-hover:translate-y-4 transition-transform flex items-end pt-4 px-6 overflow-hidden">
                   <div className="flex items-end justify-between w-full h-full gap-2">
                      <div className="w-full bg-amber-400/80 rounded-t-lg h-[40%] group-hover:h-[50%] transition-all duration-500"></div>
                      <div className="w-full bg-amber-500/80 rounded-t-lg h-[70%] group-hover:h-[85%] transition-all duration-500 delay-75"></div>
                      <div className="w-full bg-emerald-500/80 rounded-t-lg h-[60%] group-hover:h-[70%] transition-all duration-500 delay-100"></div>
                      <div className="w-full bg-emerald-400/80 rounded-t-lg h-[90%] group-hover:h-[100%] transition-all duration-500 delay-150"></div>
                   </div>
                </div>
             </div>

             {/* Minor Card 2 - PDV e Vendas */}
             <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/5 border border-blue-500/20 rounded-3xl p-8 lg:p-10 relative overflow-hidden group">
                <div className="relative z-10 w-full md:w-3/4">
                  <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-blue-500/30 backdrop-blur-md">
                    <MonitorSmartphone className="w-8 h-8 text-blue-400" />
                  </div>
                  <h4 className="text-2xl font-bold text-slate-100 mb-4">Frente de Caixa (PDV)</h4>
                  <p className="text-slate-400 leading-relaxed">
                    Vendas no balcão extremamente rápidas, controle de estoque ao vivo e emissão de NFC-e.
                  </p>
                </div>
                <div className="absolute -right-8 -bottom-8 w-56 h-56 bg-blue-500/10 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                   <ShoppingCart className="w-20 h-20 text-blue-500/40" />
                </div>
             </div>

             {/* Minor Card 3 (span 2 on LG) - CRM */}
             <div className="lg:col-span-2 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border border-emerald-500/20 rounded-3xl p-8 lg:p-10 flex flex-col md:flex-row items-center gap-10 group overflow-hidden">
                <div className="flex-1 relative z-10">
                   <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-emerald-500/30 backdrop-blur-md">
                     <Users className="w-8 h-8 text-emerald-400" />
                   </div>
                   <h4 className="text-2xl font-bold text-slate-100 mb-4">Gestão de Clientes Inteligente</h4>
                   <p className="text-slate-400 text-lg leading-relaxed">
                     Saiba exatamente quando foi a última visita, crie campanhas de fidelização e evite perder clientes inativos recuperando o contato pró-ativamente.
                   </p>
                </div>
                <div className="w-full md:w-1/3 relative">
                   <div className="flex flex-col gap-4 relative z-10">
                      <div className="bg-[#1e293b] p-5 rounded-2xl shadow-xl border border-slate-700 -translate-x-2 group-hover:translate-x-0 transition-transform duration-500 flex items-center gap-4">
                         <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-lg">A</div>
                         <div className="flex-1">
                           <div className="h-2.5 w-1/2 bg-slate-600 rounded-full mb-2.5"></div>
                           <div className="h-2.5 w-3/4 bg-slate-700 rounded-full"></div>
                         </div>
                      </div>
                      <div className="bg-[#1e293b] p-5 rounded-2xl shadow-xl border border-slate-700 group-hover:-translate-x-2 transition-transform duration-500 delay-75 flex items-center gap-4">
                         <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 font-bold text-lg">M</div>
                         <div className="flex-1">
                           <div className="h-2.5 w-2/3 bg-slate-600 rounded-full mb-2.5"></div>
                           <div className="h-2.5 w-1/2 bg-slate-700 rounded-full"></div>
                         </div>
                      </div>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="bg-slate-50 relative border-b border-slate-200">
        <div className="flex flex-col lg:flex-row w-full lg:min-h-[600px] xl:min-h-[640px]">
          
          {/* Left Text & Tabs */}
          <div className="w-full lg:w-[50%] xl:w-[45%] flex flex-col items-center justify-center px-6 sm:px-12 py-16 lg:py-12">
            <div className="w-full mx-auto flex flex-col items-center max-w-2xl lg:max-w-[480px] xl:max-w-[540px]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700 font-semibold text-xs sm:text-sm mb-6 tracking-wide text-center">
                <Store className="w-3.5 h-3.5 sm:w-4 sm:h-4"/> 100% Adaptável
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] xl:text-5xl font-black text-slate-900 tracking-tight mb-10 leading-tight text-center whitespace-normal sm:whitespace-nowrap">
                Feito para o seu negócio
              </h2>
              
              <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full">
                {industrias.map((ind, i) => (
                   <button 
                     key={i}
                     onClick={() => setActiveTab(i)}
                     className={`flex flex-col items-center justify-center p-4 sm:p-5 text-center rounded-[1.5rem] transition-all duration-300 border ${activeTab === i ? 'bg-white shadow-[0_8px_30px_rgb(0,0,0,0.08)] border-slate-200 scale-[1.02] z-10' : 'hover:bg-slate-200/50 border-transparent text-slate-600 hover:scale-100'}`}
                   >
                     <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-[1rem] flex items-center justify-center mb-3 sm:mb-4 transition-colors duration-300 ${activeTab === i ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'bg-slate-200 text-slate-600'}`}>
                       <div className="scale-[1.15] sm:scale-125">
                         {ind.icon}
                       </div>
                     </div>
                     <div className={`font-bold text-sm sm:text-base lg:text-lg transition-colors ${activeTab === i ? 'text-slate-900' : 'text-slate-700'}`}>
                       {ind.title}
                     </div>
                   </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Right Image Presentation */}
          <div className="w-full lg:w-[50%] xl:w-[55%] relative h-[450px] sm:h-[500px] lg:h-auto bg-[#181818] lg:rounded-l-[40px] overflow-hidden shadow-2xl flex flex-col justify-end">
             {industrias.map((ind, i) => (
                <div 
                  key={i} 
                  className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${activeTab === i ? 'opacity-100 z-10' : 'opacity-0 pointer-events-none z-0'}`}
                >
                  <img src={ind.image} alt={ind.title} className="absolute inset-0 w-full h-full object-cover opacity-50 mix-blend-overlay" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#181818] via-[#181818]/60 to-transparent"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#181818]/80 via-transparent to-transparent hidden lg:block"></div>
                  
                  <div className="absolute bottom-0 left-0 w-full p-8 sm:p-12 lg:p-16 xl:p-24">
                    <h3 className="text-4xl lg:text-5xl xl:text-6xl font-black text-white mb-6 tracking-tight drop-shadow-md select-none">{ind.title}</h3>
                    <p className="text-lg xl:text-xl text-slate-300 max-w-2xl leading-relaxed drop-shadow-sm mb-10 select-none">{ind.desc}</p>
                    <Link to={ind.href}>
                      <button className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-full font-bold transition-all shadow-lg shadow-blue-900/50 flex items-center gap-2 hover:gap-3 group">
                        Ver funcionalidades <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </Link>
                  </div>
                </div>
             ))}
          </div>
          
        </div>
      </section>

      {/* Solutions Section */}
      <section className="bg-white py-32 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl font-black text-slate-900 tracking-tight mb-6">Soluções Sob Medida</h2>
            <p className="text-lg text-slate-600">Resolva desafios complexos da sua empresa com as ferramentas de software mais modernas do mercado.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solucoes.map((sol, i) => (
              <div key={i} className={`flex flex-col sm:flex-row gap-8 bg-white p-10 rounded-3xl shadow-sm border ${sol.border} hover:shadow-xl transition-shadow`}>
                <div className={`w-20 h-20 ${sol.bg} rounded-2xl flex items-center justify-center shrink-0`}>
                  {sol.icon}
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-slate-900 mb-3">{sol.title}</h4>
                  <p className="text-slate-600 leading-relaxed text-lg">{sol.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="bg-slate-900 text-white py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=2000&q=80')] opacity-5 object-cover mix-blend-overlay"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl font-black tracking-tight mb-6">Recursos que Transformam</h2>
            <p className="text-lg text-slate-400">Integrações poderosas que economizam milhares de horas da sua equipe todo o ano.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {recursos.map((rec, i) => (
              <div key={i} className="bg-slate-800/50 backdrop-blur-sm rounded-3xl p-10 border border-slate-700/50 hover:bg-slate-800 transition-colors text-center shadow-2xl">
                <div className="mx-auto w-24 h-24 bg-slate-700/50 rounded-full flex items-center justify-center mb-8 border border-slate-600/50">
                  {rec.icon}
                </div>
                <h4 className="font-bold text-xl text-white mb-4">{rec.title}</h4>
                <p className="text-slate-400 leading-relaxed">{rec.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

const BlogSection = () => {
   const mainPosts = [
     { ...heroPosts.main, id: heroPosts.main.slug },
     { ...heroPosts.topRight, id: heroPosts.topRight.slug },
     { ...heroPosts.bottomRight1, id: heroPosts.bottomRight1.slug }
   ];

   return (
   <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-end mb-12 border-b border-slate-100 pb-6">
           <div>
             <h2 className="text-3xl font-black text-slate-900 mb-2">Blog da Oficina</h2>
             <p className="text-slate-500">Dicas de gestão, mecânica e mercado automotivo.</p>
           </div>
           <Link to="/blog">
             <Button variant="link" className="font-bold">Ver todos os artigos &rarr;</Button>
           </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
           {mainPosts.map((post) => (
             <Link to={`/blog/${post.id}`} key={post.id} className="group cursor-pointer">
               <div className="aspect-video bg-slate-100 rounded-xl mb-4 overflow-hidden border border-slate-200">
                  <img src={post.image} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500" alt={post.title} />
               </div>
               <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">{post.category}</div>
               <h4 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-700 transition line-clamp-2">{post.title}</h4>
               <p className="text-slate-500 text-sm line-clamp-3">
                  {post.author} &bull; {post.date}
               </p>
             </Link>
           ))}
        </div>
      </div>
   </section>
   );
};

const InstagramSection = () => {
   return (
      <section className="py-24 bg-slate-50 border-t border-slate-100">
         <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
               <div>
                  <h2 className="text-3xl font-black text-slate-900 mb-2 flex items-center gap-3">
                     <Instagram className="w-8 h-8 text-pink-600" />
                     Siga nosso Instagram
                  </h2>
                  <p className="text-slate-500">Acompanhe o dia a dia e dicas exclusivas em @rostechoficial</p>
               </div>
               <a href="https://www.instagram.com/rostechoficial/" target="_blank" rel="noopener noreferrer" className="mt-4 sm:mt-0">
                  <Button variant="outline" className="border-pink-200 text-pink-700 hover:bg-pink-50">
                     Seguir @rostechoficial
                  </Button>
               </a>
            </div>
         </div>
      </section>
   );
};

